import React, {
  useEffect,
  useState,
  useContext,
  KeyboardEvent,
  useCallback,
} from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DocNode } from '@atlaskit/adf-schema';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { ReactRenderer } from '@atlaskit/renderer';
import { colors, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { DraftContext } from 'src/components/bbc-atlaskit-editor/index';
import { encode } from 'src/components/conversation-provider/serializer';
import { Editor } from 'src/components/editor/Editor';
import {
  withDescriptionAnalytics,
  EditorSource,
} from 'src/components/editor/with-analytics';
import { htmlTransformer, toJson } from 'src/components/fabric-html-renderer';
import {
  rendererTTRSeverityTracking,
  unsupportedContentLevelsTracking,
} from 'src/constants/renderer-tracking';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { useFlag } from 'src/hooks/flag';
import { UPDATE_DESCRIPTION } from 'src/redux/pull-request/actions';
import { getIsPullRequestDescriptionUpdatePending } from 'src/redux/pull-request/selectors';
import { getUserAiOnboardingFlagDismissed } from 'src/sections/global/components/flags/user-ai-onboarding/user-ai-onboarding-flag';
import urls from 'src/sections/repository/urls';
import { getCurrentFabricUser } from 'src/selectors/conversation-selectors';
import { getIsAIEnabledForWorkspace } from 'src/selectors/feature-selectors';

import { getDraftLocalStorageKey } from '../utils';

import { findInTree, isInteractiveElement } from './utils/render-element-utils';
import { usePullRequestHashesAndRepos } from './view-entire-file/hooks';

export type EditableDescriptionProps = {
  descriptionHtml: string;
  isContainingPanelExpanded?: boolean;
  panelId: string;
  containerId: string;
};

/**
 * wrapper that handles clicks on renderer to switch to edit
 * mode. don't allow switching to edit mode while update in-progress
 * by using "isDisabled"
 */
/* stylelint-disable a11y/media-prefers-reduced-motion */
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression
const RendererWrapper = styled.div<{ isDisabled: boolean }>`
  ${({ isDisabled }) => isDisabled && `pointer-events: none;`}
  transition: background 0.2s;
  &:focus,
  &:hover {
    background: ${token('elevation.surface.hovered', colors.N30)};
  }

  @media screen and (prefers-reduced-motion: reduce) {
    /* https://css-tricks.com/revisiting-prefers-reduced-motion-the-reduced-motion-media-query/ */
    transition-duration: 0.001ms;
  }
`;

const GRID_SIZE = gridSize();

const EmptyState = styled.div({
  color: token('color.text.subtlest', colors.N300),
  padding: `${GRID_SIZE}px ${GRID_SIZE / 2}px`,
});

const messages = {
  emptyStateContent: {
    id: 'frontbucket.repository.pullrequest.descEmptyStateContent',
    description: 'Placeholder text when no description is provided',
    defaultMessage: 'Add a description...',
  },
};

const BaseEditableDescription = ({
  descriptionHtml,
  isContainingPanelExpanded,
  panelId,
  containerId,
}: EditableDescriptionProps) => {
  const { handleEditMode } = useContext(DraftContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [mouseDownSelection, setMouseDownSelection] = useState('');
  const isDescriptionUpdatePending = useSelector(
    getIsPullRequestDescriptionUpdatePending
  );
  const workspaceAiEnabled = useSelector(getIsAIEnabledForWorkspace);
  const { showFlagComponent } = useFlag({ enableForPdvs: true });
  const currentUser = useSelector(getCurrentFabricUser);
  const dispatch = useDispatch();
  const parsedHtml = htmlTransformer.parse(descriptionHtml);
  const originalDoc = toJson(parsedHtml) as DocNode;
  const [currentDoc, setCurrentDoc] = useState(originalDoc);

  const draftLocalStorageKey = getDraftLocalStorageKey(
    containerId,
    currentUser?.id
  );

  const { destinationRepoFullSlug, destinationHash, sourceHash } =
    usePullRequestHashesAndRepos();

  const summarisePrDescriptionUrl = urls.api.internal.repoAiDesc(
    destinationRepoFullSlug,
    sourceHash,
    destinationHash
  );

  const { additionalConversationProviders } = useConversationsContext();

  const onEscapeHandler = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      setIsEditMode(false);
    }
  }, []);

  useEffect(() => {
    // switch to read mode when panel is collapsed
    // prevents Esc key event capturing due to effect above
    if (!isContainingPanelExpanded) {
      setIsEditMode(false);
    }
  }, [isContainingPanelExpanded]);

  useEffect(() => {
    if (handleEditMode) {
      handleEditMode(isEditMode);
    }
  }, [isEditMode, handleEditMode]);

  /**
   * When the user is deselecting text on the screen by clicking, if they are clicking outside
   * the current selection, by the time the onclick handler is called the window.getSelection()
   * value will already be cleared.
   * The mousedown callback is called before the selection is cleared.
   */
  const onMouseDownEditView = () => {
    const windowSelection = window.getSelection();
    setMouseDownSelection(
      windowSelection !== null ? windowSelection.toString() : ''
    );
  };

  const handleWrapperOnClick = (event: React.MouseEvent) => {
    // Prevent anonymous users from being able to open the description editor
    if (!currentUser) {
      return;
    }
    const targetElement = event.target as HTMLElement;
    if (!(targetElement instanceof window.Element)) {
      return;
    }
    const rendererWrapper = event.currentTarget as HTMLElement;

    // Check if the click was on an interactive element
    if (findInTree(targetElement, rendererWrapper, isInteractiveElement)) {
      return;
    }

    // Ensure that selecting text in the renderer doesn't trigger editor view.
    const windowSelection = window.getSelection();
    const selection: string | undefined =
      windowSelection !== null ? windowSelection.toString() : undefined;
    const hasSelection = selection && selection.length !== 0;

    const hasSelectionMouseDown =
      mouseDownSelection && mouseDownSelection.length !== 0;
    const allowEditBasedOnSelection = !hasSelection && !hasSelectionMouseDown;

    if (allowEditBasedOnSelection) {
      event.preventDefault();
      setIsEditMode(true);
    }
  };

  // Allows users to enter edit mode using keyboard only
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent anonymous users from being able to open the description editor
    if (!currentUser) {
      return;
    }
    // Check to see if Space or Enter were pressed
    if (
      e.key === 'Enter' ||
      e.key === ' ' ||
      // "Spacebar" for IE support
      e.key === 'Spacebar'
    ) {
      // Prevent the default action to stop scrolling when space is pressed
      e.preventDefault();
      setIsEditMode(true);
    }
  };

  const updateDescription = (descriptionDoc: any) => {
    // Save copy of the updated description so we can optimistically show
    // the new description while request is sending
    setCurrentDoc(descriptionDoc);

    // API accepts raw (markdown) description for update
    const rawDescription = encode(descriptionDoc);
    dispatch({
      type: UPDATE_DESCRIPTION.REQUEST,
      payload: {
        description: rawDescription,
      },
    });
    setIsEditMode(false);
  };

  return isEditMode ? (
    <Editor
      defaultValue={currentDoc}
      user={currentUser || undefined}
      isExpanded
      isEditing
      onCancel={() => {
        setIsEditMode(false);
      }}
      onOpen={() => {
        if (!getUserAiOnboardingFlagDismissed() && workspaceAiEnabled) {
          showFlagComponent('user-ai-onboarding');
        }
      }}
      onSave={updateDescription}
      showBeforeUnloadWarning
      allowEmptyValue
      draftLocalStorageKey={draftLocalStorageKey}
      onEscapeHandler={onEscapeHandler}
      multiCommentReview={false}
      allowRule
      summarisePrDescriptionUrl={summarisePrDescriptionUrl}
    />
  ) : (
    <RendererWrapper
      isDisabled={isDescriptionUpdatePending || !currentUser}
      data-testid="renderer-wrapper"
      onClick={handleWrapperOnClick}
      onKeyDown={handleKeyDown}
      onMouseDown={onMouseDownEditView}
      role="textbox"
      tabIndex={0}
      aria-multiline="true"
      aria-labelledby={panelId}
    >
      {currentDoc.content.length &&
      // another case for empty state
      !(
        currentDoc.content.length === 1 &&
        'content' in currentDoc.content[0] &&
        currentDoc.content[0].content?.length === 0
      ) ? (
        <ReactRenderer
          appearance="comment"
          document={currentDoc}
          analyticsEventSeverityTracking={rendererTTRSeverityTracking}
          unsupportedContentLevelsTracking={unsupportedContentLevelsTracking}
          useSpecBasedValidator
          dataProviders={additionalConversationProviders}
          schema={defaultSchema}
          allowCopyToClipboard
        />
      ) : (
        <EmptyState>
          <FormattedMessage {...messages.emptyStateContent} />
        </EmptyState>
      )}
    </RendererWrapper>
  );
};

export const EditableDescription = withDescriptionAnalytics(
  BaseEditableDescription,
  EditorSource.PR_DESC
);
